import {useUpdatedRef} from '@sail/react';
import {noop, once} from '@sail/utils';
import {useMemo} from 'react';
import {UninitializedObservabilityProviderError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {useObservabilityApi} from 'src/internal/provider/ObservabilityApiProvider';

import type {IReports} from 'src/internal/errors/types';

const logUninitializedErrorOnce = once(() => {
  logErrorInDev(new UninitializedObservabilityProviderError('useReports'));
});

const defaultReports: IReports = {
  error: noop,
  warning: noop,
};

// WeakMap to store reports objects by provider instance
const providerReportsMap = new WeakMap<
  NonNullable<ReturnType<typeof useObservabilityApi>>,
  IReports
>();

/**
 * React hook to send error and warning reports to Sentry.
 *
 * It returns an object with reporting methods contextualized with the
 * Sentry project, release, tags, and extras provided by the
 * closest `<ObservabilityProvider />`.
 *
 * For example, consider the following:
 * ```tsx
 * <ObservabilityProvider project="atlas" errors={{tags: {foo: '1', bar: '1'}}}>
 *    ...
 *    const {reportError} = useReports();
 *    reportError(error, {
 *      tags: {
 *        foo: '2'
 *      }
 *    });
 *    ...
 * </ObservabilityProvider>
 * ```
 *
 * The actual data sent to Sentry will be:
 * ```tsx
 * {
 *   project: "atlas",
 *   tags: {
 *     foo: '2',
 *     bar: '1'
 *   },
 * }
 * ```
 *
 * @example Basic {{include "./examples/useReports.basic.tsx"}}
 *
 * @see https://sail.stripe.me/apis/observability/useReports
 */
export default function useReports(): IReports {
  const api = useObservabilityApi();
  const apiRef = useUpdatedRef(api);

  return useMemo((): IReports => {
    const currentApiValue = apiRef.current;

    if (!currentApiValue) {
      logUninitializedErrorOnce();

      return defaultReports;
    }

    // Check if we already have reports for this provider
    let reports = providerReportsMap.get(currentApiValue);

    if (!reports) {
      reports = {
        error(...args: Parameters<IReports['error']>): void {
          return apiRef.current?.reports?.error(...args);
        },

        warning(...args: Parameters<IReports['warning']>): void {
          return apiRef.current?.reports?.warning(...args);
        },
      };

      providerReportsMap.set(currentApiValue, reports);
    }

    return reports;
  }, [
    apiRef, // This is a ref object, so it never changes and thus the returned object is stable.
  ]);
}
